import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import config from "../config";

const track = (name, props = {}) => {
  if (typeof window !== "undefined" && window.clarity) {
    window.clarity("event", name, props);
  }
};

const isLogoLike = (src = "") => {
  const s = (src || "").toLowerCase();
  return (
    s.includes("logo") ||
    s.includes("dpiit") ||
    s.includes("startup") ||
    s.includes("ceiit")
  );
};

const getSignupUrl = (plan, code) => {
  const baseUrl =
    process.env.REACT_APP_SIGNUP_URL || config.signUpUrl || "/signup";
  return `${baseUrl}?plan=${plan}&code=${code}`;
};

const PRIME_YEARLY_URL = getSignupUrl(9, "PRIME-YEAR-2999");
const UNLIMITED_URL = getSignupUrl(10, "UNLIMITED-9999");

const DEFAULT_PRIME_IMAGE = "/myboq_prime_yearly_split.png";
const DEFAULT_UNLIMITED_IMAGE = "/myboq_unlimited_lifetime_split.png";
const DEFAULT_DESKTOP_IMAGE = "/PrimeYearly_Unlimited_Slider_V2.png";
const DEFAULT_MOBILE_IMAGE = "/PrimeYearly_mobile_Unlimited_Slider_V2.png";
const FALLBACK_IMAGE = "/hero-infra.png";

const Slider = () => {
  const [slides, setSlides] = useState([]);
  const [current, setCurrent] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/slides.json`)
      .then((res) => res.json())
      .then((data) => setSlides(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Failed to load slides:", err));
  }, []);

  const validSlides = useMemo(
    () =>
      slides.filter((slide) => {
        if (slide.type === "content" && slide.title) return true;
        if (slide.type === "image" && (slide.image || slide.src)) return true;
        if (slide.type === "image-only" && (slide.image || slide.src))
          return true;
        if (slide.type === "pricing-split") return true;
        return false;
      }),
    [slides],
  );

  const next = () => {
    if (!validSlides.length) return;
    setCurrent((prev) => (prev + 1) % validSlides.length);
  };

  const previous = () => {
    if (!validSlides.length) return;
    setCurrent((prev) => (prev - 1 + validSlides.length) % validSlides.length);
  };

  const goToSlide = (index) => {
    if (!validSlides.length) return;
    setCurrent(index);
    track("slider_dot_click", { slideIndex: index + 1 });
  };

  useEffect(() => {
    if (isPaused || !validSlides.length) return undefined;

    timerRef.current = setTimeout(next, 6000);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [current, isPaused, validSlides.length]);

  const goSignupSameTab = (placement = "slider") => {
    track("apply_cta_click", {
      placement,
      target: "signup",
      mode: "external_same_tab",
    });
    window.location.href = config.signUpUrl;
  };

  const goToPricingPlan = (url, planName, placement) => {
    track("slider_pricing_click", {
      placement,
      plan: planName,
      mode: "same_tab",
    });
    window.location.href = url;
  };

  if (!validSlides.length) return null;

  const slide = validSlides[current];

  const isImageOnlySlide = slide.type === "image-only";
  const isPricingSplitSlide = slide.type === "pricing-split";

  const image = slide.image || slide.src || FALLBACK_IMAGE;
  const mobileImage = slide.mobileImage || image;

  const title = slide.title || "myBOQ";
  const subtitle =
    slide.subtitle || "Digital Infrastructure Estimation Platform";
  const description =
    slide.description ||
    "Create SSR-compliant, standardized, audit-ready estimates and reports faster.";
  const buttonText = slide.buttonText || "Get Started";
  const buttonLink = slide.buttonLink || "/signup";
  const externalLink = slide.externalLink;

  return (
    <section className="px-3 pt-3 sm:px-4 lg:px-6">
      <div
        className="relative mx-auto max-w-7xl overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-2xl shadow-slate-200/70"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-white to-slate-50" />

        <div
          className={`relative grid grid-cols-1 overflow-hidden min-h-[520px] lg:min-h-[470px] ${
            isImageOnlySlide || isPricingSplitSlide ? "" : "lg:grid-cols-2"
          }`}
        >
          {isPricingSplitSlide ? (
            <div className="relative h-[520px] w-full overflow-hidden bg-gradient-to-br from-slate-50 via-white to-orange-50 lg:h-[470px]">
              <div className="grid h-full grid-cols-1 gap-3 p-3 sm:gap-4 sm:p-4 lg:grid-cols-2">
                <button
                  type="button"
                  aria-label="Start Prime Yearly plan"
                  onClick={() =>
                    goToPricingPlan(
                      slide.desktopLinkLeft || PRIME_YEARLY_URL,
                      "Prime Yearly",
                      "pricing_split_prime_yearly",
                    )
                  }
                  className="group relative min-h-0 overflow-hidden rounded-3xl border border-orange-200 bg-white shadow-xl shadow-orange-100/60 transition-all duration-300 hover:-translate-y-1 hover:border-orange-400 hover:shadow-2xl hover:shadow-orange-200/80 hover:ring-4 hover:ring-orange-300/30 active:scale-[0.995]"
                >
                  <img
                    src={slide.leftImage || DEFAULT_PRIME_IMAGE}
                    alt={
                      slide.leftAlt ||
                      "myBOQ Prime Yearly ₹1 per estimate plan"
                    }
                    className="h-full w-full object-cover object-left transition-transform duration-700 ease-out group-hover:scale-[1.035]"
                    loading={current === 0 ? "eager" : "lazy"}
                    fetchPriority={current === 0 ? "high" : "auto"}
                    onError={(e) => {
                      e.currentTarget.src = FALLBACK_IMAGE;
                    }}
                  />

                  <div className="absolute inset-0 bg-gradient-to-tr from-orange-500/10 via-transparent to-white/20 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

                  <div className="absolute left-4 top-4 rounded-2xl border border-white/70 bg-white/85 px-4 py-3 text-left shadow-xl shadow-slate-900/10 backdrop-blur-md">
                    <p className="text-[11px] font-black uppercase tracking-[0.16em] text-orange-700">
                      Prime Yearly
                    </p>
                    <p className="mt-1 text-sm font-extrabold text-slate-950 sm:text-base">
                      Start at ₹1 per Estimate
                    </p>
                    <p className="mt-0.5 text-xs font-semibold text-slate-600">
                      Click to get started
                    </p>
                  </div>
                </button>

                <button
                  type="button"
                  aria-label="Go Unlimited Lifetime plan"
                  onClick={() =>
                    goToPricingPlan(
                      slide.desktopLinkRight || UNLIMITED_URL,
                      "Unlimited Lifetime",
                      "pricing_split_unlimited",
                    )
                  }
                  className="group relative min-h-0 overflow-hidden rounded-3xl border border-amber-300 bg-slate-950 shadow-xl shadow-slate-300/70 transition-all duration-300 hover:-translate-y-1 hover:border-amber-300 hover:shadow-2xl hover:shadow-amber-200/70 hover:ring-4 hover:ring-amber-300/30 active:scale-[0.995]"
                >
                  <img
                    src={slide.rightImage || DEFAULT_UNLIMITED_IMAGE}
                    alt={slide.rightAlt || "myBOQ Unlimited Lifetime plan"}
                    className="h-full w-full object-cover object-right transition-transform duration-700 ease-out group-hover:scale-[1.035]"
                    loading={current === 0 ? "eager" : "lazy"}
                    fetchPriority={current === 0 ? "high" : "auto"}
                    onError={(e) => {
                      e.currentTarget.src = FALLBACK_IMAGE;
                    }}
                  />

                  <div className="absolute inset-0 bg-gradient-to-tr from-amber-400/15 via-transparent to-white/10 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

                  <div className="absolute left-4 top-4 rounded-2xl border border-white/20 bg-slate-950/70 px-4 py-3 text-left shadow-xl shadow-slate-950/20 backdrop-blur-md">
                    <p className="text-[11px] font-black uppercase tracking-[0.16em] text-amber-300">
                      Lifetime Access
                    </p>
                    <p className="mt-1 text-sm font-extrabold text-white sm:text-base">
                      Unlimited Estimates
                    </p>
                    <p className="mt-0.5 text-xs font-semibold text-slate-200">
                      Click to go unlimited
                    </p>
                  </div>
                </button>
              </div>
            </div>
          ) : isImageOnlySlide ? (
            <div className="relative h-[520px] w-full overflow-hidden bg-gradient-to-br from-slate-50 via-white to-orange-50 lg:h-[470px]">
              <picture>
                <source
                  media="(max-width: 767px)"
                  srcSet={mobileImage || DEFAULT_MOBILE_IMAGE}
                />
                <img
                  src={image || DEFAULT_DESKTOP_IMAGE}
                  alt={
                    slide.alt ||
                    "myBOQ Prime Yearly and Unlimited Lifetime pricing plans"
                  }
                  className="h-full w-full object-contain"
                  loading={current === 0 ? "eager" : "lazy"}
                  fetchPriority={current === 0 ? "high" : "auto"}
                  onError={(e) => {
                    e.currentTarget.src = FALLBACK_IMAGE;
                  }}
                />
              </picture>

              <div className="pointer-events-none absolute left-4 top-4 z-20 sm:left-5 sm:top-5">
                <div className="rounded-2xl border border-white/60 bg-white/85 px-4 py-3 shadow-xl shadow-slate-900/10 backdrop-blur-md transition-all duration-300 lg:px-5">
                  <p className="text-[11px] font-black uppercase tracking-[0.16em] text-orange-700">
                    Limited Pricing Offer
                  </p>
                  <p className="mt-1 text-sm font-extrabold text-slate-950 sm:text-base">
                    Start at ₹1 per Estimate
                  </p>
                  <p className="mt-0.5 text-xs font-semibold text-slate-600">
                    Upgrade to Unlimited anytime
                  </p>
                </div>
              </div>

              <button
                type="button"
                aria-label="Start Prime Yearly plan"
                onClick={() =>
                  goToPricingPlan(
                    slide.desktopLinkLeft || PRIME_YEARLY_URL,
                    "Prime Yearly",
                    "desktop_left_pricing_image",
                  )
                }
                className="absolute inset-y-0 left-0 hidden w-1/2 cursor-pointer bg-transparent transition hover:bg-orange-500/5 lg:block"
              />

              <button
                type="button"
                aria-label="Go Unlimited Lifetime plan"
                onClick={() =>
                  goToPricingPlan(
                    slide.desktopLinkRight || UNLIMITED_URL,
                    "Unlimited Lifetime",
                    "desktop_right_pricing_image",
                  )
                }
                className="absolute inset-y-0 right-0 hidden w-1/2 cursor-pointer bg-transparent transition hover:bg-slate-950/5 lg:block"
              />

              <button
                type="button"
                aria-label="Start Prime Yearly plan"
                onClick={() =>
                  goToPricingPlan(
                    slide.mobileLinkTop || PRIME_YEARLY_URL,
                    "Prime Yearly",
                    "mobile_top_pricing_image",
                  )
                }
                className="absolute inset-x-0 top-0 h-1/2 cursor-pointer bg-transparent transition hover:bg-orange-500/5 lg:hidden"
              />

              <button
                type="button"
                aria-label="Go Unlimited Lifetime plan"
                onClick={() =>
                  goToPricingPlan(
                    slide.mobileLinkBottom || UNLIMITED_URL,
                    "Unlimited Lifetime",
                    "mobile_bottom_pricing_image",
                  )
                }
                className="absolute inset-x-0 bottom-0 h-1/2 cursor-pointer bg-transparent transition hover:bg-slate-950/5 lg:hidden"
              />
            </div>
          ) : (
            <>
              <div className="relative z-10 flex flex-col justify-center p-7 sm:p-9 lg:p-12">
                <div className="inline-flex w-fit items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs font-extrabold uppercase tracking-wide text-orange-700">
                  🇮🇳 Built in Maharashtra • MH PWD SSR
                </div>

                <h2 className="mt-5 max-w-xl text-3xl font-black leading-tight tracking-tight text-slate-950 sm:text-4xl lg:text-5xl">
                  {title}
                </h2>

                {subtitle && (
                  <p className="mt-4 max-w-xl text-base font-bold leading-7 text-slate-700 sm:text-lg">
                    {subtitle}
                  </p>
                )}

                {description && (
                  <p className="mt-4 max-w-xl text-sm leading-7 text-slate-600 sm:text-base">
                    {description}
                  </p>
                )}

                <div className="mt-7 flex flex-wrap gap-3">
                  {buttonLink === "/signup" ? (
                    <button
                      type="button"
                      onClick={() => goSignupSameTab("slider_primary")}
                      className="inline-flex items-center justify-center rounded-full bg-orange-600 px-6 py-3 text-sm font-extrabold text-white shadow-lg shadow-orange-500/25 transition hover:-translate-y-0.5 hover:bg-orange-700"
                    >
                      {buttonText}
                    </button>
                  ) : buttonLink ? (
                    <Link
                      to={buttonLink}
                      onClick={() =>
                        track("slider_cta_click", { target: buttonLink })
                      }
                      className="inline-flex items-center justify-center rounded-full bg-orange-600 px-6 py-3 text-sm font-extrabold text-white shadow-lg shadow-orange-500/25 transition hover:-translate-y-0.5 hover:bg-orange-700"
                    >
                      {buttonText}
                    </Link>
                  ) : externalLink ? (
                    <a
                      href={externalLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        track("slider_cta_click", { target: externalLink })
                      }
                      className="inline-flex items-center justify-center rounded-full bg-orange-600 px-6 py-3 text-sm font-extrabold text-white shadow-lg shadow-orange-500/25 transition hover:-translate-y-0.5 hover:bg-orange-700"
                    >
                      {buttonText}
                    </a>
                  ) : null}

                  {!String(buttonText || "")
                    .toLowerCase()
                    .includes("watch") && (
                    <a
                      href="https://www.youtube.com/watch?v=waJAgQUF6j8&t=292s"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-6 py-3 text-sm font-extrabold text-slate-900 shadow-sm transition hover:-translate-y-0.5 hover:bg-slate-50"
                      onClick={() =>
                        track("demo_video_click", {
                          placement: "slider_secondary",
                        })
                      }
                    >
                      ▶ Watch Demo
                    </a>
                  )}
                </div>

                <div className="mt-7 flex flex-wrap gap-2 text-xs font-semibold text-slate-600">
                  <span className="rounded-full border border-slate-200 bg-white px-3 py-1.5">
                    ✅ SSR-Compliant
                  </span>
                  <span className="rounded-full border border-slate-200 bg-white px-3 py-1.5">
                    🏛️ Government-ready outputs
                  </span>
                  <span className="rounded-full border border-slate-200 bg-white px-3 py-1.5">
                    🤖 AI-ready roadmap
                  </span>
                </div>
              </div>

              <div className="relative h-[300px] overflow-hidden lg:h-[470px]">
                <div className="absolute inset-0 bg-gradient-to-br from-slate-100 to-white" />

                {isLogoLike(image) ? (
                  <div className="absolute inset-0 flex items-center justify-center p-6">
                    <div className="flex w-full max-w-md items-center justify-center rounded-3xl border border-slate-200 bg-white p-8 shadow-xl shadow-slate-200/70">
                      <img
                        src={image}
                        alt={slide.alt || "myBOQ slide visual"}
                        className="max-h-[190px] w-auto object-contain"
                        loading={current === 0 ? "eager" : "lazy"}
                        onError={(e) => {
                          e.currentTarget.src = FALLBACK_IMAGE;
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <img
                      src={image}
                      alt={slide.alt || "myBOQ slide visual"}
                      className="absolute inset-0 h-full w-full object-cover"
                      loading={current === 0 ? "eager" : "lazy"}
                      onError={(e) => {
                        e.currentTarget.src = FALLBACK_IMAGE;
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/45 via-slate-950/10 to-transparent" />
                    <div className="absolute left-4 top-4 rounded-full border border-white/40 bg-white/90 px-4 py-2 text-xs font-extrabold text-slate-800 shadow-sm backdrop-blur">
                      Audit-ready • Standardized • Faster approvals
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {validSlides.length > 1 && (
          <>
            <button
              type="button"
              aria-label="Previous slide"
              onClick={previous}
              className="absolute left-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/40 bg-white/80 text-slate-800 shadow-md backdrop-blur transition hover:bg-white lg:flex"
            >
              ‹
            </button>

            <button
              type="button"
              aria-label="Next slide"
              onClick={next}
              className="absolute right-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/40 bg-white/80 text-slate-800 shadow-md backdrop-blur transition hover:bg-white lg:flex"
            >
              ›
            </button>

            <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-2 rounded-full border border-white/30 bg-white/80 px-3 py-2 shadow-sm backdrop-blur">
              {validSlides.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={`Go to slide ${i + 1}`}
                  onClick={() => goToSlide(i)}
                  className={`h-2.5 rounded-full transition-all ${
                    i === current
                      ? "w-8 bg-orange-600"
                      : "w-2.5 bg-slate-300 hover:bg-slate-400"
                  }`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default Slider;